'use client';

import { useEffect } from 'react';

// The standard <div google-add-preferred-source-btn> implementation has
// Google's publisher.js script inject a real button (attributes + iframe)
// into that div before React hydrates it, which causes a hydration mismatch
// and gets the injected content wiped out when React "fixes" it. Driving our
// own button through the documented manual JS API avoids that entirely —
// Google's script never touches this DOM node.
// https://developers.google.com/search/docs/appearance/preferred-sources

declare global {
  interface Window {
    PREFERRED_SOURCE?: Array<(preferredSource: PreferredSourceController) => void>;
  }
}

interface PreferredSourceController {
  init: (options: { theme?: 'light' | 'dark'; lang?: string }) => void;
  addPreferredSource: () => void;
}

const BUTTON_ID = 'preferred-source-btn';

export function PreferredSourceButton({ theme = 'dark' }: { theme?: 'light' | 'dark' }) {
  useEffect(() => {
    const button = document.getElementById(BUTTON_ID);
    if (!button) return;

    let cleanup: (() => void) | undefined;

    window.PREFERRED_SOURCE = window.PREFERRED_SOURCE || [];
    window.PREFERRED_SOURCE.push((preferredSource) => {
      preferredSource.init({ theme });
      const handleClick = () => preferredSource.addPreferredSource();
      button.addEventListener('click', handleClick);
      cleanup = () => button.removeEventListener('click', handleClick);
    });

    return () => cleanup?.();
  }, [theme]);

  return (
    <button
      id={BUTTON_ID}
      type="button"
      className="text-sm text-gray-500 hover:text-white transition-colors underline underline-offset-2"
    >
      Add Hustlance to your Preferred Sources
    </button>
  );
}
